import { useMemo } from 'react';

import { createTooltipRenderer } from '../components/Tooltip';
import { CreateTooltipRenderFnParams } from '../components/Tooltip/tooltipRenderer';
import { TooltipRenderFn } from '../utils/ChartProps';
import { DataKeyType } from '../utils/DataProps';

export interface UseTooltipRendererProps<TDataKeyType extends DataKeyType, TValueType extends number, TDatum>
  extends Pick<
    CreateTooltipRenderFnParams<TValueType, TDataKeyType, TDatum>,
    | 'dataKeyGetter'
    | 'descriptionRenderer'
    | 'labelGetter'
    | 'valueGetter'
    | 'title'
    | 'showTotal'
    | 'showPercentage'
    | 'showTime'
    | 'showOtherItems'
  > {
  /**
   * Overriding Tooltip Renderer. If not provided, default renderer will be used.
   */
  tooltipRenderer?: TooltipRenderFn<TDataKeyType, TDatum> | false;
}

/**
 * Hook to produce a TooltipRenderer to be used when interacting with chart components
 */
export const useTooltipRenderer = <TDataKeyType extends DataKeyType, TValueType extends number, TDatum>({
  dataKeyGetter,
  descriptionRenderer,
  labelGetter,
  valueGetter,
  tooltipRenderer: tooltipRendererProp,
  title,
  showTotal,
  showPercentage,
  showTime,
  showOtherItems,
}: UseTooltipRendererProps<TDataKeyType, TValueType, TDatum>): TooltipRenderFn<TDataKeyType, TDatum> | undefined => {
  const tooltipRenderer = useMemo(() => {
    if (tooltipRendererProp) {
      return tooltipRendererProp;
    }

    if (tooltipRendererProp === undefined) {
      return createTooltipRenderer<TValueType, TDataKeyType, TDatum>({
        dataKeyGetter,
        descriptionRenderer,
        labelGetter,
        valueGetter,
        title,
        showTotal,
        showPercentage,
        showTime,
        showOtherItems,
      });
    }

    return undefined;
  }, [
    dataKeyGetter,
    descriptionRenderer,
    labelGetter,
    showOtherItems,
    showPercentage,
    showTime,
    showTotal,
    title,
    tooltipRendererProp,
    valueGetter,
  ]);

  return tooltipRenderer;
};
